import { apiClient } from "./api";
import type { AppSchema } from "@shared/types";

export interface AppRecord {
  id: string;
  tenant_id: string;
  name: string;
  version: string;
  status: string;
  app_schema: AppSchema;
  created_at: string;
  updated_at: string;
}

export async function listApps(): Promise<AppRecord[]> {
  const response = await apiClient.get("/apps");
  return response.data;
}

export async function getApp(id: string): Promise<AppRecord> {
  const response = await apiClient.get(`/apps/${id}`);
  return response.data;
}

export async function createApp(name: string, schema: AppSchema): Promise<AppRecord> {
  const response = await apiClient.post("/apps", { name, app_schema: schema });
  return response.data;
}

export async function updateApp(id: string, name: string, schema: AppSchema): Promise<AppRecord> {
  const response = await apiClient.put(`/apps/${id}`, { name, app_schema: schema });
  return response.data;
}

/**
 * 发布应用：以当前 name + schema 调用 PUT /apps/{id}，并在请求体携带
 * `status: "active"`。注意：当前后端 AppUpdateRequest 只消费 name/app_schema，
 * status 字段被服务端忽略（状态流转为后续 Wave 的后端能力），因此 UI 以
 * 响应记录中的 status 为准展示 draft/active。
 */
export async function publishApp(id: string, name: string, schema: AppSchema): Promise<AppRecord> {
  const response = await apiClient.put(`/apps/${id}`, {
    name,
    app_schema: schema,
    status: "active",
  });
  return response.data;
}

export async function deleteApp(id: string): Promise<void> {
  await apiClient.delete(`/apps/${id}`);
}

export async function applyDataModels(appId: string, dataModels: AppSchema["dataModels"]): Promise<{ applied: string[]; errors: string[] }> {
  const response = await apiClient.post(`/apps/${appId}/apply-data-models`, { dataModels });
  return response.data;
}

export async function executeAppWorkflow(
  appId: string,
  workflow: AppSchema["workflows"][number],
  payload: Record<string, unknown>,
): Promise<{ status: string; logs: Record<string, unknown>[] }> {
  const response = await apiClient.post(`/apps/${appId}/workflows/execute`, { workflow, payload });
  return response.data;
}

export async function exportApp(appId: string, filename: string): Promise<void> {
  const response = await apiClient.get(`/apps/${appId}/export`, {
    responseType: "blob",
  });
  const blob = new Blob([response.data as BlobPart], { type: "application/zip" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
